<script setup lang="ts">
import { FormSectionConfig, FormValues } from "../types";
import FormEmbeddedTable from "./FormEmbeddedTable.vue";

const props = defineProps<{
  section: FormSectionConfig;
  modelValue: Record<string, any>;
  loading?: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: any): void;
}>();

const value = computed({
  get: () => props.modelValue,
  set: (value: any) => emit("update:modelValue", value),
});

// Filter conditional fields
const conditionalFields = computed(() => {
  return props.section.fields.filter((field) => {
    if (field.conditional) {
      // Assess the conditional field
      return field.conditional(value.value);
    }
    return true;
  });
});

const disabled = computed(() => {
  if (props.section.disabled === true || props.loading || props.disabled) {
    return true;
  } else if (typeof props.section.disabled === "function") {
    return props.section.disabled(value.value);
  }
  return false;
});
</script>

<template>
  <VRow>
    <VCol cols="12">
      <h4>{{ typeof section.title === 'function' ? section.title(value) : section.title ? $t(section.title) : "" }}</h4>
    </VCol>

    <VCol
      v-for="field in conditionalFields"
      :cols="field.size?.xs ?? 12"
      :sm="field.size?.sm"
      :md="field.size?.md"
      :lg="field.size?.lg"
      :xl="field.size?.xl"
    >
      <FormInputText
        v-if="field.type === 'text'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputStrings
        v-if="field.type === 'strings'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputNumber
        v-else-if="field.type === 'number'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputTextArea
        v-else-if="field.type === 'textarea'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputSelect
        v-else-if="field.type === 'select'"
        :config="field"
        :form-values="value"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputCheckBox
        v-else-if="field.type === 'checkbox'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputSwitch
        v-else-if="field.type === 'switch'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputDate
        v-else-if="field.type === 'date'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputTime
        v-else-if="field.type === 'time'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputDateTime
        v-else-if="field.type === 'datetime'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputDateRange
        v-else-if="field.type === 'daterange'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputSlider
        v-else-if="field.type === 'slider'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputAutoComplete
        v-else-if="field.type === 'autocomplete'"
        :config="field"
        :form-values="value"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputMedia
        v-else-if="field.type === 'media' || field.type === 'avatar'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormInputRating
        v-else-if="field.type === 'rating'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
      <FormNested
        v-else-if="field.type === 'nested-form'"
        :config="field"
        v-model="value"
        :loading="loading"
      />
      <FormInputButton
        v-else-if="field.type === 'button'"
        :config="field"
        :value="value"
        :disabled="disabled"
      />
      <FormEmbeddedTable
        v-else-if="field.type === 'table'"
        :config="field"
        :value="value"
        :loading="loading"
      />
      <FormInputTable
        v-else-if="field.type === 'input-table'"
        :config="field"
        v-model="value[field.name]"
        :form-values="value"
        :disabled="disabled"
      />
      <FormCustomComponent
        v-else-if="field.type === 'custom'"
        :config="field"
        v-model="value[field.name]"
        :disabled="disabled"
      />
    </VCol>
  </VRow>
</template>
